import { create } from "zustand";
import { devtools, persist, subscribeWithSelector } from "zustand/middleware";

import { CHECKOUT_STORAGE_KEY, WALLEY_PAYMENT_TYPE } from "./config/constants";
import type { Address, DeliveryAddress, DeliveryOption } from "./types";

export type CheckoutStore = {
  counter: number;
  address: Partial<Address>;
  ableToComplete: boolean;
  paymentType: typeof WALLEY_PAYMENT_TYPE.b2c | typeof WALLEY_PAYMENT_TYPE.b2b;
  hasLine2Address: boolean;
  deliveryAddress: Partial<DeliveryAddress>;
  deliveryOption?: DeliveryOption;
  hasSeparateDeliveryAddress: boolean;
  canScrollToDelivery: boolean;
  hasSetAddress: boolean;
  setAddress: (newAddress: Partial<Address>) => Promise<unknown>;
  setAbleToComplete: (value: boolean) => void;
  setPaymentType: (
    paymentType:
      | typeof WALLEY_PAYMENT_TYPE.b2c
      | typeof WALLEY_PAYMENT_TYPE.b2b,
  ) => void;
  setDeliveryOption: (deliveryOption?: DeliveryOption) => void;
  setDeliveryAddress: (
    newDeliveryAddress: Partial<DeliveryAddress>,
  ) => Promise<unknown>;
  setHasLine2Address: (hasLine2Address: boolean) => void;
  setHasSeparateDeliveryAddress: (hasSeparateDeliveryAddress: boolean) => void;
  setCanScrollToDelivery: (canScrollToDelivery: boolean) => void;
  destroy: () => void;
};

export const checkoutStore = create<CheckoutStore>()(
  devtools(
    subscribeWithSelector(
      persist(
        (set) => ({
          counter: 0,
          address: {},
          ableToComplete: true,
          paymentType: WALLEY_PAYMENT_TYPE.b2c,
          deliveryOption: undefined,
          deliveryAddress: {},
          hasLine2Address: false,
          hasSeparateDeliveryAddress: false,
          canScrollToDelivery: false,
          hasSetAddress: false,
          setAddress: (newAddress) => {
            return Promise.resolve(
              set((state) => {
                return {
                  counter: state.counter + 1,
                  hasSetAddress: true,
                  address: {
                    ...state.address,
                    ...newAddress,
                    zipCode: newAddress.zipCode?.trim(),
                  },
                };
              }),
            );
          },
          setAbleToComplete: (value: boolean) => {
            if (value === true) {
              set({ ableToComplete: true });
              return;
            }

            set({
              ableToComplete: false,
              deliveryOption: undefined,
            });
          },
          setHasLine2Address: (hasLine2Address: boolean) => {
            set({ hasLine2Address });
          },
          setDeliveryAddress: (newDeliveryAddress) => {
            return Promise.resolve(
              set((state) => {
                return {
                  counter: state.counter + 1,
                  deliveryAddress: {
                    ...state.deliveryAddress,
                    ...newDeliveryAddress,
                    deliveryZipCode: newDeliveryAddress.deliveryZipCode?.trim(),
                  },
                };
              }),
            );
          },
          setHasSeparateDeliveryAddress: (
            hasSeparateDeliveryAddress: boolean,
          ) => {
            set({ hasSeparateDeliveryAddress });
          },
          setDeliveryOption: (deliveryOption) => {
            if (!deliveryOption) {
              set((state) => ({
                deliveryOption: undefined,
                counter: state.counter + 1,
              }));
              return;
            }
            set((state) => ({ deliveryOption, counter: state.counter + 1 }));
          },
          setPaymentType: (paymentType) => {
            set(() => ({ paymentType }));
          },
          setCanScrollToDelivery: (canScrollToDelivery) => {
            set(() => ({ canScrollToDelivery }));
          },
          destroy: () =>
            set({
              deliveryOption: undefined,
              ableToComplete: false,
              hasSetAddress: false,
              counter: 0,
            }),
        }),
        {
          merge: (persistedState: unknown, currentState) => {
            const persisted = persistedState as Partial<CheckoutStore>;
            const newState = {
              ...currentState,
              ...persisted,
            };
            if (!newState.hasSetAddress) {
              newState.deliveryOption = undefined;
            }
            return newState;
          },
          name: CHECKOUT_STORAGE_KEY,
          partialize: (state) => {
            return Object.fromEntries(
              Object.entries(state).filter(([key]) => {
                return [
                  "address",
                  "paymentType",
                  "deliveryOption",
                  "deliveryAddress",
                  "hasSeparateDeliveryAddress",
                  "hasSetAddress",
                ].includes(key);
              }),
            );
          },
        },
      ),
    ),
  ),
);
